import { defineStore } from "pinia";
import {
  getProducts,
  getProductsTaxon,
  getProductsByTaxonId,
  getSortProducts,
  PostFavoriteProduct,
  DeleteFavoriteProduct,
  getProduct,
  getProductVariant,
  searchProductsByTag,
  GetFavoriteProducts,
} from "@/api/auth";
import { productsService } from "@/api/services/productsService";
import { useRegisterStore } from "./register";
import { useDelegueStore } from "./delegueStore";

export const useProductsStore = defineStore("products", {
  state: () => ({
    products: [],
    products_filter: [],
    allProducts: [],
    favorites: [],
    firstcall: true,
    loading: false,
    loadingFilter: false,
    error: null,
    rationProducts: [],
    selectedRationProduct: null,
    selectedRationVariant: null,
    rationLoading: false,
    rationError: null,
    selectedVariantTarifTtc: null,
    selectedVariantPriceLoading: false,
    reminderProducts: [],
    currentPage: 1,
    itemsPerPage: 12, // Default to 8 for pagination
    totalProducts: 0, // To track total products from API
    searchQuery: "", // To store the current search query
    loadingFavorites: false,
  }),
  getters: {
    countFavorite: state => state.favorites.length,
    Products: state => state.products ?? [],
    AllProducts: state => state.allProducts ?? [],
    getFavoriteProducts: (state) => state.favorites,
    ProductsFiltered: state => state.products_filter ?? [],
    availableRationProducts: state => {
      return state.rationProducts.filter(product => product.enabled !== false);
    },
    availableRationVariants: state => {
      if (!state.selectedRationProduct) return [];
      const variants = state.selectedRationProduct.variantsDetails || [];
      // Filtrer uniquement les variants avec unity === "u"
      return variants.filter(variant => variant.unity === "u");
    },
    hasRationProducts: state => state.rationProducts.length > 0,
    isRationLoading: state => state.rationLoading,
    availableReminderProducts: state => {
      return state.reminderProducts.filter(product => product.enabled !== false);
    },
    hasReminderProducts: state => state.reminderProducts.length > 0,
    formatPrice: () => priceInCents => {
      if (!priceInCents) return "Prix non disponible";
      return `${(priceInCents / 100).toFixed(2)} €`;
    },
    formattedTarifTtc: state => {
      if (state.selectedVariantTarifTtc === null) return null;
      if (state.selectedVariantTarifTtc === undefined) return "Prix non disponible";
      return `${parseFloat(state.selectedVariantTarifTtc).toFixed(2)} €`;
    },
    isPriceLoading: state => state.selectedVariantPriceLoading,

  },
  actions: {
    async fetchProducts(WaitLoad = true) {
      if (this.firstcall && WaitLoad) this.loading = true;
      try {
         const registerStore = useRegisterStore();
         const delegueStore = useDelegueStore();
         const userRole = registerStore.user?.role || "";
         const isDelegue = userRole === "delegues";
         const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;

        const response = await getProducts( 1,  30,"","","",isDelegue, vetId);

        this.allProducts = response.data["hydra:member"] || [];
        //this.fetchFavoriteProducts();
        if (this.firstcall && WaitLoad) {
          this.firstcall = false;
          this.loading = false;
        }
      } catch (error) {
        this.error = "Erreur lors du chargement des produits";
      } finally {
        if (this.firstcall && WaitLoad) {
          this.loading = false;
        }
      }
    },
    async fetchAllProducts({ page = 1, itemsPerPage = 12, append = false } = {}, searchQuery = "",code="",orderBy) {
      this.loadingFilter = true;
      try {
        const registerStore = useRegisterStore();
         const delegueStore = useDelegueStore();
         const userRole = registerStore.user?.role || "";
         const isDelegue = userRole === "delegues";
         const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;
        const response = await getProducts(page, itemsPerPage, searchQuery,code,orderBy,isDelegue, vetId);
        const newProducts = response.data["hydra:member"] || [];
        if (append) {
          this.products = [...this.products, ...newProducts];
          this.products_filter = [...this.products_filter, ...newProducts];
        } else {
          this.products = newProducts;
          this.products_filter = newProducts;
        }
        this.totalProducts = response.data["hydra:totalItems"] || this.products.length;
        this.currentPage = page;
        this.loadingFilter = false;
        return { items: this.products, total: this.totalProducts };
      } catch (error) {
        this.error = "Erreur lors du chargement des produits";
        this.loadingFilter = false;
        return { items: [], total: 0 };
      }
    },

    async fetchProductsByTaxon(code, { page = 1, itemsPerPage = 12, orderBy = "" } = {}) {
      this.loadingFilter = true;
      try {

        const registerStore = useRegisterStore();
         const delegueStore = useDelegueStore();
         const userRole = registerStore.user?.role || "";
         const isDelegate = userRole === "delegues";
         const vetId = isDelegate ? delegueStore.getSelectedVeterinarian?.id : null;

        const response = await getProductsTaxon(code, { page, itemsPerPage, orderBy,isDelegate, vetId });
        this.products = response.data["hydra:member"] || [];
        this.products_filter = response.data["hydra:member"] || [];
        this.totalProducts = response.data["hydra:totalItems"] || this.products.length;
        this.loadingFilter = false;
        return { items: this.products, total: this.totalProducts };
      } catch (error) {
        this.error = "Erreur lors du chargement des produits par gammes";
        this.loadingFilter = false;
        return { items: [], total: 0 };
      }
    },

    async fetchProductsByTag(tag, { page = 1, itemsPerPage = 12 } = {}) {
      this.loadingFilter = true;
      try {
        const registerStore = useRegisterStore();
         const delegueStore = useDelegueStore();
         const userRole = registerStore.user?.role || "";
         const isDelegue = userRole === "delegues";
         const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;
        const { data, total, pages } = await searchProductsByTag(tag, { page, itemsPerPage },isDelegue, vetId);
        this.products = data;
        this.products_filter = data;
        this.totalProducts = total;
        this.loadingFilter = false;
        return { items: data, total, pages };
      } catch (error) {
        this.error = "Erreur lors de la recherche par tag";
        this.loadingFilter = false;
        return { items: [], total: 0, pages: 0 };
      }
    },
    async fetchProductsByTaxonId(taxonId, page = 1, itemsPerPage = 12) {
      this.loading = true;
      try {
        const response = await getProductsByTaxonId(taxonId, page, itemsPerPage);
        if (response.data["hydra:member"]) {
          this.products = response.data["hydra:member"] || [];
          this.products_filter = response.data["hydra:member"] || [];
          this.totalProducts = response.data["hydra:totalItems"] || this.products.length;
        } else {
          this.products = response.data;
          this.products_filter = response.data;
          this.totalProducts = Array.isArray(response.data) ? response.data.length : 0;
        }
        this.loading = false;
        return { items: this.products, total: this.totalProducts };
      } catch {
        this.error = "Erreur lors du chargement des produits du taxon";
        this.loading = false;
        return { items: [], total: 0 };
      }
    },
    async fetchSortProducts(orderBy) {
      this.loadingFilter = true;
      try {
        const response = await getSortProducts(orderBy);
        this.products_filter = response.data["hydra:member"] || response.data;
        this.products = this.products_filter;
        this.totalProducts =
          response.data["hydra:totalItems"] || (Array.isArray(this.products_filter) ? this.products_filter.length : 0);
        this.loadingFilter = false;
      } catch {
        this.error = "Problème lors du chargement des produits";
      }
    },
    AddFavoriteProduct(prod) {
      PostFavoriteProduct(prod.id)
        .then(() => {
          // Update has_favorite in allProducts
          const product = this.allProducts.find(p => p.id === prod.id);
          if (product) {
            product.has_favorite = true; // Update reactively
          }
          // Update favorites array
          if (!this.favorites.find(p => p.id === prod.id)) {
            this.favorites.push(prod);
          }
        })
        .catch(error => {
          console.error("Error adding favorite:", error);
        });
    },
    RemoveFavoriteProduct(prod) {
      DeleteFavoriteProduct(prod.id)
        .then(() => {
          // Update has_favorite in allProducts
          const product = this.allProducts.find(p => p.id === prod.id);
          if (product) {
            product.has_favorite = false; // Update reactively
          }
          // Update favorites array
          this.favorites = this.favorites.filter(p => p.id !== prod.id);
        })
        .catch(error => {
          console.error("Error removing favorite:", error);
        });
    },


  async fetchFavoriteProducts() {
      this.loadingFavorites = true;
      this.errorFavorites = null;

      try {
        const response = await GetFavoriteProducts();

        // EXTRACT ONLY THE 'product' FROM EACH ITEM
        const rawFavorites = response.data || [];
        this.favorites = rawFavorites.map(item => item.product).filter(Boolean);

      } catch (error) {
        console.error('Failed to fetch favorites:', error);
        this.errorFavorites = error;
        this.favorites = [];
      } finally {
        this.loadingFavorites = false;
      }
    },




    async fetchProductsByAnimalType(animalTypeId) {
      if (!animalTypeId) {
        this.rationProducts = [];
        this.resetRationSelection();
        return;
      }

      this.rationLoading = true;
      this.rationError = null;

      try {
        const data = await productsService.getProductsByAnimalType(animalTypeId);
        this.rationProducts = data;
      } catch (error) {
        this.rationError = "Erreur lors de la récupération des produits";
        console.error("Erreur fetchProductsByAnimalType:", error);
        this.rationProducts = [];
      } finally {
        this.rationLoading = false;
      }
    },

    async fetchAllReminderProducts() {
      this.rationLoading = true;
      this.rationError = null;

      try {
        const data = await productsService.getAllProducts();
        this.reminderProducts = data;
        return data;
      } catch (error) {
        this.rationError = "Erreur lors de la récupération des produits";
        console.error("Erreur fetchAllReminderProducts:", error);
        this.reminderProducts = [];
        return [];
      } finally {
        this.rationLoading = false;
      }
    },

    async fetchRationProductVariant(variantCode) {
      if (!variantCode) return null;

      this.rationLoading = true;
      this.rationError = null;

      try {
        const data = await productsService.getProductVariant(variantCode);
        return data;
      } catch (error) {
        this.rationError = "Erreur lors de la récupération du variant";
        console.error("Erreur fetchRationProductVariant:", error);
        return null;
      } finally {
        this.rationLoading = false;
      }
    },

    selectRationProduct(product) {
      this.selectedRationProduct = product;
      this.selectedRationVariant = null;

      if (product && product.variantsDetails && product.variantsDetails.length > 0) {
        // Filtrer d'abord par unity === "u", puis chercher le premier disponible
        const availableVariants = product.variantsDetails.filter(variant => variant.unity === "u");
        const firstAvailableVariant = availableVariants.find(variant => variant.enabled !== false && variant.inStock);
        if (firstAvailableVariant) {
          this.selectRationVariant(firstAvailableVariant);
        }
      }
    },

    async selectRationVariant(variant) {
      this.selectedRationVariant = variant;

      // Réinitialiser le tarif TTC lors du changement de variant
      this.selectedVariantTarifTtc = null;

      // Récupérer automatiquement le prix si un variant est sélectionné
      if (variant && variant.id) {
        await this.fetchProductVariantPrice(variant.id);
      }
    },

    async fetchProductVariantPrice(variantId) {
      if (!variantId) {
        this.selectedVariantTarifTtc = null;
        return;
      }

      this.selectedVariantPriceLoading = true;

      try {
        const priceData = await productsService.getProductVariantPrice(variantId);
        // La réponse contient 'priceTtc' selon les logs
        this.selectedVariantTarifTtc = priceData.priceTtc || priceData.tarifTtc || priceData.price || null;
      } catch (error) {
        console.error("Erreur lors de la récupération du prix du variant:", error);
        this.selectedVariantTarifTtc = null;
        // Ne pas bloquer l'interface en cas d'erreur
      } finally {
        this.selectedVariantPriceLoading = false;
      }
    },

    resetRationSelection() {
      this.selectedRationProduct = null;
      this.selectedRationVariant = null;
    },

    resetRationState() {
      this.rationProducts = [];
      this.selectedRationProduct = null;
      this.selectedRationVariant = null;
      this.rationLoading = false;
      this.rationError = null;
      this.selectedVariantTarifTtc = null;
      this.selectedVariantPriceLoading = false;
    },

    getRationReferenceObject() {
      if (!this.selectedRationProduct || !this.selectedRationVariant) {
        return null;
      }

      return {
        product: this.selectedRationProduct,
        variant: this.selectedRationVariant,
        productId: this.selectedRationProduct.id,
        variantCode: this.selectedRationVariant.code,
        label: `${this.selectedRationProduct.name} - ${this.selectedRationVariant.name}`,
        image: this.selectedRationProduct.images?.[0]?.path || this.selectedRationProduct.imagesDetails?.[0]?.path,
        price: this.selectedRationVariant.currentChannelPricings?.price,
      };
    },

   async searchProducts(query, page = 1, itemsPerPage = 12,code,orderBy = "") {
  
      this.loadingFilter = true;
      try {

        const registerStore = useRegisterStore();
         const delegueStore = useDelegueStore();
         const userRole = registerStore.user?.role || "";
         const isDelegue = userRole === "delegues";
         const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;

        const response = await getProducts(page, itemsPerPage, query,code, orderBy,isDelegue, vetId); // Pass query as third param
        const results = response.data["hydra:member"] || [];
        this.products = results;
        this.products_filter = results;
        this.totalProducts = response.data["hydra:totalItems"] || results.length;
        this.currentPage = page;
        return { items: results, total: this.totalProducts };
      } catch (error) {
        this.error = "Erreur lors de la recherche des produits";
        return { items: [], total: 0 };
      } finally {
        this.loadingFilter = false;
      }
    }
  },
});
export const useProductStore = defineStore("product", {
  state: () => ({
    product: null,
    loading_product: false,
    loading_variant: false,
    error: null,
  }),
  actions: {
    async DetailProduct(slug) {
      this.loading_product = true;
      try {

        const registerStore = useRegisterStore();
          const delegueStore = useDelegueStore();
          const userRole = registerStore.user?.role || "";
          const isDelegue = userRole === "delegues";
          const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;

        const response = await getProduct(slug,isDelegue, vetId);

        this.product = response.data;
        this.loading_product = false;
      } catch {
        this.error = "Erreur lors du chargement du produit";
      }
    },
    async getVariant(code) {
      this.loading_variant = true;
      try {
        const res = await getProductVariant(code);
        this.loading_variant = false;
        return res.data;
      } catch (error) {
        console.error("Error fetching variant", error);
      } finally {
        this.loading_variant = false;
      }
    },
  },
});
